import { useCallback, useMemo, useState } from 'react';
import type React from 'react';

import { isSsr } from '@change-corgi/core/ssr';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { useLoginOrSignupPageData, useLoginOrSignupPageState } from 'src/app/pages/loginOrSignup/pageContext';
import {
	initializeLoginSignupProps,
	useGotoRedirect,
	useLoginOrSignupState,
	useLoginOrSignupTracking,
	useLoginUserFlow,
} from 'src/app/pages/loginOrSignup/shared/hooks';
import { GoogleAuthButton } from 'src/app/shared/components/buttons';
import type { GoogleLoginResultData } from 'src/app/shared/hooks/auth';

export const GoogleButton: React.FC = () => {
	const context = useUtilityContext();
	const { translate } = useI18n();
	const [trackedError, setTrackedError] = useState(false);
	const [, { setError }] = useLoginOrSignupPageState();
	const { setNewGDPRUserCreatedViaSocialAuth, gotoStep } = useLoginOrSignupState();
	const { trackLoginError } = useLoginOrSignupTracking();
	const userFlow = useLoginUserFlow();
	const optionalOnClickTrackProps = useMemo(
		(): TrackingProperties => ({
			// facebook is not available server-side
			...(!isSsr() ? initializeLoginSignupProps(context.facebook) : undefined),
			user_flow: userFlow,
		}),
		[context.facebook, userFlow],
	);
	const gotoRedirectTo = useGotoRedirect();
	const {
		data: {
			featureConfigs: { futureSapMarketingConsentRequired: isFutureSapMarketingConsentRequired },
		},
	} = useLoginOrSignupPageData();

	const onSuccess = useCallback(
		(data: GoogleLoginResultData) => {
			if (!data.existingUser && isFutureSapMarketingConsentRequired) {
				setNewGDPRUserCreatedViaSocialAuth(true);
				gotoStep('marketing_consent');
			} else {
				gotoRedirectTo({ loginOrJoinMethod: 'manual_google' });
			}
		},
		[gotoStep, gotoRedirectTo, isFutureSapMarketingConsentRequired, setNewGDPRUserCreatedViaSocialAuth],
	);

	const onError = useCallback(() => {
		if (!trackedError) {
			trackLoginError('manual_google', 'unknown');
			setTrackedError(true);
		}
		setError(translate('corgi.login_or_signup.error.google.generic'));
	}, [trackedError, setError, translate, trackLoginError]);

	return (
		<GoogleAuthButton
			onSuccess={onSuccess}
			onError={onError}
			optionalOnClickTrackProps={optionalOnClickTrackProps}
			loginType={userFlow}
			signupContext="login_or_signup_social_auth"
			data-qa="google-auth-button"
		/>
	);
};
